import { useForm, Controller } from 'react-hook-form';
import {
  Typography,
  TextField as MuiTextField,
  Button as MuiButton,
  FormControl,
  Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatPhoneNumber } from 'utility/helpers';
import { SyntheticEvent, useEffect } from 'react';
import { RegistrationFormData, titleOptions } from '@buildwise/libs/signup';
import * as Yup from 'yup';
import useUserData from 'hooks/useUserData';

const EditProfileSchema = Yup.object().shape({
  firstname: Yup.string()
    .required('Required field')
    .min(2, 'The first name must contain at least 2 characters')
    .nullable(),
  lastname: Yup.string()
    .required('Required field')
    .min(1, 'The last name must be at least one character')
    .nullable(),
  email: Yup.string().email('Enter a valid email').required('Required field'),
  title: Yup.string().required('Required field'),
  primaryCompanyName: Yup.string()
    .required('Required field')
    .min(2, 'Company name must contain at least 2 characters'),
});

export type GetStartedFormData = Partial<RegistrationFormData>;

type GetStartedFormProps = {
  procore: any;
  onSubmit: (values: GetStartedFormData) => Promise<void>;
};

const GetStartedForm = ({ onSubmit, procore }: GetStartedFormProps) => {
  const { user } = useUserData({
    shouldFetchCompanyDetails: false,
    shouldFetchUserDetails: false,
  });
  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
    setValue,
  } = useForm<GetStartedFormData>({
    mode: 'onTouched',
    resolver: yupResolver(EditProfileSchema),
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (user) {
      reset(getDefaultValues(), { keepDirty: true, keepIsValid: true });
    }
  }, [user]);

  function getDefaultValues() {
    return {
      firstname: user?.firstname || procore?.firstname || procore?.firstName || '',
      lastname: user?.lastname || procore?.lastname || procore?.lastName || '',
      email: user?.username || user?.email || procore?.username || procore?.email || '',
      phoneNumber: user?.phoneNumber || procore?.phoneNumber || '',
      title: user?.title || procore?.title || '',
      primaryCompanyName:
        user?.primaryCompanyName || procore?.primaryCompanyName || procore?.companyName || '',
    };
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <WelcomeTitle>Welcome to BuildWise!</WelcomeTitle>
      <Description>Complete your profile to get started.</Description>

      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState?.error?.message || ''}
              {...field}
              variant="outlined"
              placeholder="First Name"
              autoFocus
            />
          </FormControl>
        )}
        name="firstname"
      />

      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState?.error?.message || ''}
              {...field}
              variant="outlined"
              placeholder="Last Name"
            />
          </FormControl>
        )}
        name="lastname"
      />

      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              {...field}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState?.error?.message || ''}
              disabled
              tabIndex={-1}
              variant="outlined"
              placeholder="Email"
            />
          </FormControl>
        )}
        name="email"
      />

      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              fullWidth
              helperText={fieldState?.error?.message || ''}
              {...field}
              onChange={(e) => {
                field.onChange(formatPhoneNumber(e.target.value));
              }}
              variant="outlined"
              placeholder="Phone Number (optional)"
            />
          </FormControl>
        )}
        name="phoneNumber"
      />

      <Controller
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              {...field}
              freeSolo
              autoHighlight
              value={field.value || null}
              onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                setValue('title', value || '', { shouldDirty: true, shouldValidate: true })
              }
              onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                setValue('title', value || '', { shouldDirty: true, shouldValidate: true })
              }
              options={titleOptions}
              noOptionsText="No Job Titles Found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  placeholder="Job Title"
                />
              )}
            />
          </FormControl>
        )}
        name="title"
      />

      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth>
            <TextField
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState?.error?.message || ' '}
              {...field}
              variant="outlined"
              disabled={
                !!(user?.primaryCompanyId && user?.primaryCompanyName) ||
                !!(
                  procore?.primaryCompanyId &&
                  (procore?.primaryCompanyName || procore?.companyName)
                )
              }
              placeholder="Company Name"
            />
          </FormControl>
        )}
        name="primaryCompanyName"
      />

      <Button fullWidth variant="contained" disabled={isSubmitting} type="submit" sx={{ mt: 1 }}>
        {isSubmitting ? 'Saving...' : 'Get Started'}
      </Button>
    </Form>
  );
};

const WelcomeTitle = styled(Typography, { label: 'WelcomeTitle' })(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  color: theme.palette.custom.mirage,
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
  },
}));

const Description = styled(Typography, { label: 'Description' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.riverBed,
  margin: '8px 0 32px',
}));

const Form = styled('form', { label: 'Form' })(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.black,
      },
    },
  },
}));

const Button = styled(MuiButton, { label: 'Button' })({
  borderRadius: 8,
  height: 56,
});

const TextField = styled(MuiTextField, { label: 'TextField' })(({ theme }) => ({
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: theme.palette.custom.inputBackground,
  },
}));

export default GetStartedForm;
